import { api, query } from '../../api/client';
import type { AnalyticsQuery, ModelSeries, QuerySource, ThroughputSeries } from './types';
import type {
  OverviewTotals,
  PoolKey,
  ProviderHealth,
  RealmShare,
  ReconciliationDay,
  TenantProviderKey,
  TenantUsageTotal,
} from '../../data/types';

/** Lane B: the warehouse, and the two collections a browser must never read.
 *
 *  Charts are aggregates over millions of rows and take seconds, so they are a
 *  request/response lane rather than a watch — and a browser must never hold
 *  BigQuery credentials, so the aggregate is computed server-side and only the
 *  series crosses the wire. The pool and BYO-key reads are here for a different
 *  reason: a Firestore rule grants a whole document, and both of those
 *  collections carry raw credential material, so no rule can make them safe. */

const ADMIN = '/api/v1/admin';

function scope(q: AnalyticsQuery) {
  return query({ range: q.range, estateId: q.estateId, tenantId: q.tenantId });
}

export function createHttpQuery(): QuerySource {
  return {
    kind: 'query',

    totals: (q) => api.get<OverviewTotals>(`${ADMIN}/analytics/totals${scope(q)}`),
    throughput: (q) => api.get<ThroughputSeries>(`${ADMIN}/analytics/throughput${scope(q)}`),
    tokensByModel: (q) => api.get<ModelSeries>(`${ADMIN}/analytics/tokens-by-model${scope(q)}`),
    usageByTenant: (q) => api.get<TenantUsageTotal[]>(`${ADMIN}/analytics/usage-by-tenant${scope(q)}`),
    providerHealth: (q) => api.get<ProviderHealth[]>(`${ADMIN}/analytics/provider-health${scope(q)}`),
    realmMix: (q) => api.get<RealmShare[]>(`${ADMIN}/analytics/realm-mix${scope(q)}`),
    reconciliation: (q) => api.get<ReconciliationDay[]>(`${ADMIN}/analytics/reconciliation${scope(q)}`),
  };
}

/** listPoolKeys reads the credential pool through the control plane.
 *
 *  The response is a projection with no payload and no key reference — built
 *  field by field server-side, so it cannot start carrying a secret because a
 *  json tag changed upstream. */
export async function listPoolKeys(): Promise<PoolKey[]> {
  const res = await api.get<{ keys: PoolKey[] }>(`${ADMIN}/pool-keys`);
  return res.keys ?? [];
}

/** listTenantKeys reads a tenant's sealed BYO keys — metadata only. */
export async function listTenantKeys(
  estateId: string,
  tenantId: string,
): Promise<TenantProviderKey[]> {
  const res = await api.get<{ keys: TenantProviderKey[] }>(
    `${ADMIN}/estates/${encodeURIComponent(estateId)}/tenants/${encodeURIComponent(tenantId)}/provider-keys`,
  );
  return res.keys ?? [];
}

/** Tenant is the registry record, including the physical BigQuery table its
 *  rows live in — which is what an operator needs when a chart is empty. */
export interface TenantRecord {
  estateId: string;
  tenantId: string;
  displayName: string;
  ordinal: number;
  table: string;
}

export async function listTenants(estateId?: string): Promise<TenantRecord[]> {
  const res = await api.get<{ tenants: TenantRecord[] }>(
    `${ADMIN}/tenants${query({ estateId })}`,
  );
  return res.tenants ?? [];
}
